<template>
  <ContactModal
    :from="from"
    :isVisible="isShowingContactModal"
    :modalType="modalType"
    :trackingLabel="trackingLabel"
    :title="modalTitle"
    :subTitle="modalSubTitle"
    @onClose="handleCloseContactModal"
    @onSubmit="handleSubmitContactForm"
  />
  <ContactFormSubmitResultNotification
    :isShowingNotificationToast="isShowingNotificationToast"
    :isContactFormSubmitSuccessfully="isContactFormSubmitSuccessfully"
    @clear="clearToast"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import ContactFormSubmitResultNotification from '@/components/common/ContactModal/ContactFormSubmitResultNotification.vue';
import ContactModal from '@/components/common/ContactModal/ContactModal.vue';
import { useAuth0Client } from '@/composables/useAuth0Client';
import { isPlatformIs24 } from '@/helpers/Platform/Platform';
import { mapToContactRequest, submitContactForm } from '@/store/modules/localLanding/services';
import type { ContactRequest } from '@/store/modules/localLanding/types';
import { ContactModalFrom, ContactModalType } from '@/types';
import { Auth0VueClient } from '@auth0/auth0-vue';
import { Source } from '@smg-real-estate/seller-leads-schema';
import { useI18n } from 'vue-i18n';

export interface SubmitOptions {
  /**
   * When true, builds the contact request with property details from Vuex (propertyDetails)
   * and Auth0 user. Must be a constant for the component's lifetime.
   */
  isPropertyDetailsIncluded?: boolean;
}

function getDefaultMapper(): (payload: ContactRequest) => ContactRequest {
  return (payload) => mapToContactRequest(payload);
}

const store = useStore();
const propertyData = computed(() => store.getters['propertyDetails/getPropertyData']);
const auth0Client = useAuth0Client();
const userInfo = computed(
  () => (auth0Client as Auth0VueClient)?.user?.value ?? { name: '', email: '' },
);
function getPropertyDetailsMapper(): (payload: ContactRequest) => ContactRequest {
  return (payload: ContactRequest) => {
    const source = isPlatformIs24() ? Source.Is24PropEvaluationHODB : Source.HgPropEvaluationHODB;
    return mapToContactRequest(payload, propertyData.value, userInfo.value, source);
  };
}

const props = defineProps<{
  from: ContactModalFrom;
  modalType: ContactModalType;
  submitOptions?: SubmitOptions;
  trackingLabel?: string;
}>();
const { t } = useI18n();

const modalTitle = computed(() => {
  const key =
    props.modalType === ContactModalType.AGENCY_RECOMMENDATION
      ? 'localLanding.contactForm.agencyRecommendation.title'
      : 'localLanding.contactForm.callMeBack.title';
  return t(key);
});

const modalSubTitle = computed(() => {
  const key =
    props.modalType === ContactModalType.AGENCY_RECOMMENDATION
      ? 'localLanding.contactForm.agencyRecommendation.subTitle'
      : 'localLanding.contactForm.callMeBack.subTitle';
  return t(key);
});

const { isPropertyDetailsIncluded = false } = props.submitOptions ?? {};

const isShowingContactModal = ref(false);
const isShowingNotificationToast = ref(false);
const isContactFormSubmitSuccessfully = ref(false);

const handleCloseContactModal = (): void => {
  isShowingContactModal.value = false;
};

const handleSubmitContactForm = async (modalPayload: ContactRequest): Promise<void> => {
  const buildContactRequest = isPropertyDetailsIncluded
    ? getPropertyDetailsMapper()
    : getDefaultMapper();

  const contactRequestData = buildContactRequest(modalPayload);
  const isSuccess = await submitContactForm(contactRequestData);

  if (typeof isSuccess === 'boolean') {
    isShowingNotificationToast.value = true;
    isContactFormSubmitSuccessfully.value = isSuccess;
  }
  handleCloseContactModal();
};

const clearToast = (): void => {
  isShowingNotificationToast.value = false;
};

function open(): void {
  isShowingContactModal.value = true;
}

defineExpose({ open });
</script>
